import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { QUERY_GC_TIME_MS, QUERY_MAX_RETRIES, QUERY_STALE_TIME_MS } from '@/constants'

import { type ApiError, toApiError } from './api-error'

declare module '@tanstack/react-query' {
  interface Register {
    defaultError: ApiError
    queryMeta: {
      /** Show a toast when this query fails (off by default: screens show their own error state). */
      errorToast?: boolean
    }
    mutationMeta: {
      /** Set to false when the screen reports the failure itself. */
      showErrorToast?: boolean
    }
  }
}

/** Retry a failed read only when trying again could help: not for 4xx answers, not for a cancel, and not forever. */
const shouldRetry = (failureCount: number, error: ApiError) => !error.isClientError && !error.isCancelled && failureCount < QUERY_MAX_RETRIES

export function createQueryClient() {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        if (query.meta?.errorToast) toast.error(toApiError(error).message)
      },
    }),
    mutationCache: new MutationCache({
      // One place decides how a failed write is reported, so no form has to remember to.
      onError: (error, _variables, _context, mutation) => {
        const failure = toApiError(error)
        if (mutation.meta?.showErrorToast === false || failure.isCancelled) return
        toast.error(failure.message)
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: QUERY_STALE_TIME_MS,
        gcTime: QUERY_GC_TIME_MS,
        // Nothing is asked for again just because the window was clicked: a screen that needs fresh data says so itself (the comments do).
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
        retry: shouldRetry,
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
      },
      // A write that timed out may still have worked, so it is never repeated automatically.
      mutations: { retry: false },
    },
  })
}

/**
 * The app's one client. Components reach it through `useQueryClient()`; code outside React (the sign-in step, the commands that
 * span several calls) imports it directly, so both share the same cache.
 */
export const queryClient = createQueryClient()
